import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { api, unwrap } from '../../../api/client';
import { errorMessage } from '../../../api/errors';
import type { components } from '../../../api/schema';
import { useAuth } from '../../../auth/AuthProvider';
import { Button, Card, Field, Input, Select } from '../../../design';
import { useToast } from '../../../design/Toast';
import { useOrgOptions } from '../../users/UsersPage';
import { type LockerDetail, type LockerType, TYPE_LABEL } from '../common';
import { MiniMap } from '../../map/MiniMap';
import s from '../lockers.module.css';

type LockerUpdate = components['schemas']['LockerUpdate'];

export function LocationTab({ locker: l }: { locker: LockerDetail }) {
  const { user, has } = useAuth();
  const canEdit = has('configure');
  const qc = useQueryClient();
  const toast = useToast();
  const orgs = useOrgOptions();
  const form = useForm<LockerUpdate>({
    defaultValues: {
      name: l.name,
      serialNumber: l.serialNumber,
      simIccid: l.simIccid,
      comment: l.comment,
      maintenanceNote: l.maintenanceNote,
      location: l.location,
      orgId: l.orgId,
      type: l.type,
    },
  });
  const save = useMutation({
    mutationFn: (d: LockerUpdate) =>
      unwrap(
        api.PATCH('/lockers/{id}', {
          params: { path: { id: l.id } },
          body: {
            name: d.name,
            serialNumber: d.serialNumber,
            simIccid: d.simIccid ? d.simIccid : null,
            comment: d.comment,
            maintenanceNote: d.maintenanceNote,
            location: d.location
              ? { ...d.location, lat: Number(d.location.lat), lng: Number(d.location.lng) }
              : undefined,
            ...(d.orgId !== l.orgId ? { orgId: d.orgId } : {}),
            ...(d.type !== l.type ? { type: d.type } : {}),
          },
        }),
      ),
    onSuccess: () => {
      toast('Lockerul a fost actualizat.', 'success');
      void qc.invalidateQueries({ queryKey: ['locker', l.id] });
      void qc.invalidateQueries({ queryKey: ['lockers'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const maintenance = useMutation({
    mutationFn: (on: boolean) =>
      unwrap(
        api.PATCH('/lockers/{id}', { params: { path: { id: l.id } }, body: { maintenance: on } }),
      ),
    onSuccess: (_x, on) => {
      toast(
        on
          ? 'Lockerul este în mentenanță; comenzile și cheile noi sunt blocate.'
          : 'Mentenanța a fost încheiată.',
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['locker', l.id] });
      void qc.invalidateQueries({ queryKey: ['lockers'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const lat = Number(useWatch({ control: form.control, name: 'location.lat' }));
  const lng = Number(useWatch({ control: form.control, name: 'location.lng' }));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16 }}>
      <Card>
        <h2>Identitate și locație</h2>
        <form
          className="stack"
          style={{ marginTop: 12 }}
          onSubmit={form.handleSubmit((d) => save.mutate(d))}
        >
          <div className={s.formGrid}>
            <Field label="Nume" htmlFor="l-name">
              <Input
                id="l-name"
                disabled={!canEdit}
                {...form.register('name', { required: true, minLength: 2 })}
              />
            </Field>
            <Field label="Serie" htmlFor="l-sn">
              <Input
                id="l-sn"
                disabled={!canEdit}
                {...form.register('serialNumber', { required: true, minLength: 3 })}
              />
            </Field>
            <Field label="ICCID SIM" htmlFor="l-sim">
              <Input id="l-sim" disabled={!canEdit} {...form.register('simIccid')} />
            </Field>
            <Field label="Tip" hint="Refuzat cât există operațiuni active" htmlFor="l-type">
              <Select id="l-type" disabled={!canEdit} {...form.register('type')}>
                {(Object.keys(TYPE_LABEL) as LockerType[]).map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            {user?.orgId === null && (
              <Field
                label="Organizație"
                hint="Transferul e refuzat cât există operațiuni active"
                htmlFor="l-org"
              >
                <Select id="l-org" disabled={!canEdit} {...form.register('orgId')}>
                  {orgs.data?.items.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label="Oraș" htmlFor="l-city">
              <Input
                id="l-city"
                disabled={!canEdit}
                {...form.register('location.city', { required: true })}
              />
            </Field>
            <Field label="Adresă" htmlFor="l-addr">
              <Input id="l-addr" disabled={!canEdit} {...form.register('location.address')} />
            </Field>
            <Field label="Latitudine" htmlFor="l-lat">
              <Input
                id="l-lat"
                type="number"
                step="0.000001"
                disabled={!canEdit}
                {...form.register('location.lat')}
              />
            </Field>
            <Field label="Longitudine" htmlFor="l-lng">
              <Input
                id="l-lng"
                type="number"
                step="0.000001"
                disabled={!canEdit}
                {...form.register('location.lng')}
              />
            </Field>
          </div>
          <Field label="Comentarii" htmlFor="l-comment">
            <Input id="l-comment" disabled={!canEdit} {...form.register('comment')} />
          </Field>
          <Field label="Notă de mentenanță" htmlFor="l-maint">
            <Input id="l-maint" disabled={!canEdit} {...form.register('maintenanceNote')} />
          </Field>
          {canEdit && (
            <div className="flex between">
              <Button variant="primary" type="submit" busy={save.isPending}>
                Salvează
              </Button>
              {l.status === 'maintenance' ? (
                <Button onClick={() => maintenance.mutate(false)} busy={maintenance.isPending}>
                  Încheie mentenanța
                </Button>
              ) : (
                <Button
                  icon="alert"
                  onClick={() => maintenance.mutate(true)}
                  busy={maintenance.isPending}
                >
                  Pune în mentenanță
                </Button>
              )}
            </div>
          )}
        </form>
      </Card>
      <Card tight>
        <MiniMap
          lat={Number.isFinite(lat) ? lat : l.location.lat}
          lng={Number.isFinite(lng) ? lng : l.location.lng}
          status={l.status}
          label={l.name}
          onPick={
            canEdit
              ? (la, ln) =>
                  form.setValue('location', { ...form.getValues('location')!, lat: la, lng: ln })
              : undefined
          }
        />
      </Card>
    </div>
  );
}
